'use client';

/**
 * ProductGallery — Carousel for product media (video + images).
 *
 * Sprint 139 (PR-G: Product Gallery Carousel).
 *
 * Replaces the pre-PR-G gallery in
 * `frontend/src/app/market/producto/[storeSlug]/[productSlug]/page.tsx`
 * which had 3 limitations (per Explore sub-agent report):
 *   1. NO sliding between images (no carousel, no next/prev arrows, no
 *      dot indicators).
 *   2. Video + main image were MUTUALLY EXCLUSIVE — when a product had a
 *      video, the main image was suppressed entirely.
 *   3. Thumbnails were non-interactive + capped at 3 (`.slice(0, 3)`)
 *      so images #4+ were never rendered.
 *
 * This component fixes all 3:
 *   - Uses shadcn Carousel (Embla-based) for swipe + arrows + dot indicators.
 *   - Builds a unified media array: video as slide #1 (if exists) + all
 *     images as subsequent slides (no .slice cap).
 *   - Thumbnails are clickable (click → jump to that slide) + the active
 *     one is marked with a ring + aria-current.
 *
 * Spec ref: SPEC-081 Fase 3 §6.7 (public product detail page).
 *
 * §6 Anti-Invención: thin UI wrapper around the existing convertVideoUrlToEmbed
 * helper (PR #870) + shadcn Carousel. No business logic.
 *
 * §10 REGLA DE ORO: fix de raíz. Replaces the broken IIFE in the SSR page
 * with a proper carousel — no patches.
 *
 * Sprint 139 (PR-G: Product Gallery Carousel).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { PlayCircle, Package, X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { convertVideoUrlToEmbed } from '@/lib/video-url';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '@/components/ui/carousel';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export interface ProductGalleryProps {
  /** Optional video URL (YouTube/Vimeo/Loom watch URL). */
  videoUrl?: string | null;
  /** All product images (no cap — render all of them as slides). */
  images: string[];
  /** Product title (for iframe title + img alt text). */
  productTitle: string;
}

// Unified media item — either a video embed or an image URL.
type MediaItem =
  | { type: 'video'; embedUrl: string; originalUrl: string }
  | { type: 'image'; url: string }
  | { type: 'video-fallback'; originalUrl: string };

export function ProductGallery({
  videoUrl,
  images,
  productTitle,
}: ProductGalleryProps) {
  // ─── Build the unified media array ────────────────────────────────────
  //
  // Order: video first (if exists + convertible), then all images.
  // If the video URL doesn't match YouTube/Vimeo/Loom pattern, render it
  // as a "video-fallback" slide (a link "Ver en plataforma original").
  const mediaItems = useMemo<MediaItem[]>(() => {
    const items: MediaItem[] = [];
    // Sprint 139 (P-333 fix): images FIRST, video LAST (Founder directive:
    // 'aparecen de primeros en la galeria, deben ser el ultimo').
    // Pre-fix: video was slide #1 (first) — Founder wants video at the end.
    for (const url of images) {
      if (url && url.trim().length > 0) {
        items.push({ type: 'image', url });
      }
    }
    // Video goes LAST — only if it exists.
    if (videoUrl && videoUrl.trim().length > 0) {
      const embedUrl = convertVideoUrlToEmbed(videoUrl);
      if (embedUrl) {
        items.push({ type: 'video', embedUrl, originalUrl: videoUrl });
      } else {
        items.push({ type: 'video-fallback', originalUrl: videoUrl });
      }
    }
    return items;
  }, [videoUrl, images]);

  // Track the active slide index (for thumbnails active state + dot indicator).
  const [activeIndex, setActiveIndex] = useState(0);
  // Carousel API reference — used to scroll to a slide when the user clicks
  // a thumbnail (programmatic scroll), and to receive 'select' events when
  // the user swipes (sync activeIndex with the carousel state).
  const [apiRef, setApiRef] = useState<CarouselApi | null>(null);
  // Sprint 139 (Founder QA hotfix 2026-09-26): lightbox state. When open,
  // renders a full-screen modal with the clicked image at max resolution
  // (MercadoLibre-style image viewer). Pre-fix: clicking an image did
  // nothing — Founder feedback: "la galeria de imagenes no apertura como
  // en mercadolibre para que puedan ver bien las imagenes".
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Indices in `mediaItems` that are images (not videos). Used by the
  // lightbox to navigate prev/next only between image items (videos are
  // already viewable in the main carousel via their iframe).
  const imageOnlyIndices = useMemo(
    () => mediaItems
      .map((item, idx) => (item.type === 'image' ? idx : -1))
      .filter((idx) => idx >= 0),
    [mediaItems],
  );

  // Sync activeIndex with the carousel when the user swipes (Embla 'select').
  useEffect(() => {
    if (!apiRef) return;
    const onSelect = () => {
      setActiveIndex(apiRef.selectedScrollSnap());
    };
    apiRef.on('select', onSelect);
    // Initialize the active index on mount.
    onSelect();
    // Cleanup: Embla's `on` returns an `off` function to remove the listener.
    return () => {
      apiRef.off('select', onSelect);
    };
  }, [apiRef]);

  // Scroll to a slide when the user clicks a thumbnail. We use a separate
  // useEffect (not the onClick handler directly) so we can guard against
  // the case where apiRef isn't ready yet OR the target index is already
  // the active one (no-op scroll would cause infinite re-render).
  const handleThumbnailClick = useCallback(
    (index: number) => {
      if (!apiRef) {
        // Carousel not ready yet — just update the state; the initial
        // mount will set the active slide via the `opts` below... actually
        // no — we'll just wait for apiRef to be set, then the effect
        // below will scroll. For now, set state so the UI reflects intent.
        setActiveIndex(index);
        return;
      }
      if (index === apiRef.selectedScrollSnap()) return;
      apiRef.scrollTo(index);
    },
    [apiRef],
  );

  // If activeIndex was set via thumbnail click but the carousel wasn't
  // ready yet, scroll now when it becomes ready.
  useEffect(() => {
    if (apiRef && apiRef.selectedScrollSnap() !== activeIndex) {
      apiRef.scrollTo(activeIndex);
    }
  }, [apiRef, activeIndex]);

  // ─── Empty state (no media at all) ────────────────────────────────────
  if (mediaItems.length === 0) {
    return (
      <div className="flex aspect-square w-full items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground">
        <Package className="size-16" aria-hidden="true" />
      </div>
    );
  }

  // ─── Render a single slide ────────────────────────────────────────────
  const renderSlide = (item: MediaItem, index: number) => {
    if (item.type === 'video') {
      return (
        <div className="aspect-square w-full overflow-hidden bg-black">
          <iframe
            src={item.embedUrl}
            title={`Video de ${productTitle} (slide ${index + 1})`}
            className="size-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
          />
        </div>
      );
    }
    if (item.type === 'video-fallback') {
      return (
        <a
          href={item.originalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex aspect-square size-full flex-col items-center justify-center gap-2 bg-muted p-4 text-center text-sm text-muted-foreground transition-colors hover:bg-muted/50"
        >
          <PlayCircle className="size-12" aria-hidden="true" />
          <span className="font-medium">Ver video en plataforma original</span>
          <span className="text-xs text-muted-foreground/70">
            (abre en nueva pestaña)
          </span>
        </a>
      );
    }
    // image
    return (
      <button
        type="button"
        onClick={() => {
          // Sprint 139 (Founder QA hotfix 2026-09-26): clicking the main
          // image opens a full-screen lightbox (MercadoLibre-style viewer).
          setLightboxIndex(index);
          setLightboxOpen(true);
        }}
        className="group relative flex aspect-square size-full items-center justify-center overflow-hidden bg-white"
        aria-label={`Ver imagen ${index + 1} en pantalla completa`}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={item.url}
          alt={`Imagen ${index + 1} de ${productTitle}`}
          // Sprint 139 (Founder QA hotfix 2026-09-26): `object-contain` (was
          // `object-cover`) — pre-fix images were CROPPED to fill the
          // square, hiding important details (vertical product photos lost
          // the top + bottom). `object-contain` shows the full image, padded
          // by the white background. Founder feedback: "las imagenes no se
          // adaptan al tamaño de la galeria se cortan".
          className="size-full object-contain p-2 transition-transform duration-200 group-hover:scale-[1.02]"
          loading={index === 0 ? 'eager' : 'lazy'}
        />
        <span className="pointer-events-none absolute bottom-2 right-2 flex size-8 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100">
          <ZoomIn className="size-4" aria-hidden="true" />
        </span>
      </button>
    );
  };

  // ─── Main carousel ────────────────────────────────────────────────────
  //
  // Uses shadcn Carousel (Embla-based). The `setApi` callback gives us a
  // reference to the CarouselApi — we use it to receive 'select' events
  // (when the user swipes) + to programmatically scroll (when the user
  // clicks a thumbnail). See the useEffect + handleThumbnailClick above.
  // Thumbnails are rendered inline below the carousel (NOT extracted into
  // a separate const) so they have direct access to `handleThumbnailClick`
  // + `activeIndex` + `mediaItems`.
  return (
    <div className="space-y-0">
      <Carousel
        opts={{
          align: 'start',
          loop: mediaItems.length > 1,
        }}
        className="w-full overflow-hidden rounded-lg border border-border bg-muted"
        setApi={setApiRef}
      >
        <CarouselContent>
          {mediaItems.map((item, i) => (
            <CarouselItem key={`slide-${item.type}-${i}`}>
              {renderSlide(item, i)}
            </CarouselItem>
          ))}
        </CarouselContent>
        {mediaItems.length > 1 && (
          <>
            <CarouselPrevious
              className="left-2"
              aria-label="Anterior"
            />
            <CarouselNext
              className="right-2"
              aria-label="Siguiente"
            />
          </>
        )}
      </Carousel>

      {/* Thumbnails — only if more than 1 media item. Clickable to jump
          to that slide via handleThumbnailClick. */}
      {mediaItems.length > 1 && (
        <div
          className="mt-3 grid gap-2"
          style={{
            gridTemplateColumns: `repeat(${Math.min(mediaItems.length, 6)}, minmax(0, 1fr))`,
          }}
          aria-label="Miniaturas de imágenes y video"
        >
          {mediaItems.map((item, i) => (
            <button
              key={`thumb-${item.type}-${i}`}
              type="button"
              onClick={() => handleThumbnailClick(i)}
              aria-label={`Ver ${item.type === 'video' || item.type === 'video-fallback' ? 'video' : `imagen ${i + 1}`}`}
              aria-current={i === activeIndex ? 'true' : undefined}
              className={cn(
                'relative aspect-square w-full overflow-hidden rounded-md border bg-muted transition-all',
                i === activeIndex
                  ? 'border-[#06B6D4] ring-2 ring-[#06B6D4]/40'
                  : 'border-border hover:border-[#06B6D4]/40',
              )}
            >
              {item.type === 'video' ? (
                <div className="flex size-full items-center justify-center bg-gradient-to-br from-red-500/10 to-orange-500/10">
                  <PlayCircle className="size-5 text-red-600" aria-hidden="true" />
                </div>
              ) : item.type === 'video-fallback' ? (
                <div className="flex size-full items-center justify-center bg-amber-500/10">
                  <PlayCircle className="size-5 text-amber-600" aria-hidden="true" />
                </div>
              ) : (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={item.url}
                  alt={`Miniatura ${i + 1}`}
                  // Sprint 139 (Founder QA hotfix 2026-09-26): same fix as the
                  // main image — `object-contain` + bg-white so the thumbnail
                  // preview matches what the buyer sees in the main carousel.
                  className="size-full bg-white object-contain p-1"
                  loading="lazy"
                />
              )}
            </button>
          ))}
        </div>
      )}

      {/* "Video included" badge — only if a video is present */}
      {videoUrl && videoUrl.trim().length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground" aria-live="polite">
          Este producto incluye un video demostrativo.
        </p>
      )}

      {/* ─── Lightbox modal (MercadoLibre-style image viewer) ──────────── */}
      {/* Sprint 139 (Founder QA hotfix 2026-09-26): full-screen image viewer.
          Opens when the user clicks any main-carousel image. Supports
          prev/next navigation between image-only items (videos are skipped
          because the carousel already shows them). Esc closes. */}
      <Dialog
        open={lightboxOpen}
        onOpenChange={(open) => {
          setLightboxOpen(open);
        }}
      >
        <DialogContent
          className="max-w-7xl border-border bg-black/95 p-0 sm:rounded-2xl"
          // Disable the default close button — we render our own with
          // better contrast against the dark backdrop.
          showCloseButton={false}
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">
            Visor de imágenes — {productTitle}
          </DialogTitle>
          <div className="relative flex h-[85vh] w-full items-center justify-center">
            {/* Close button */}
            <button
              type="button"
              onClick={() => setLightboxOpen(false)}
              aria-label="Cerrar visor"
              className="absolute right-3 top-3 z-10 flex size-10 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
            >
              <X className="size-5" aria-hidden="true" />
            </button>

            {/* Prev (only if there are 2+ image items) */}
            {imageOnlyIndices.length > 1 && (
              <button
                type="button"
                onClick={() => {
                  const cur = imageOnlyIndices.indexOf(lightboxIndex);
                  const nextIdx = cur <= 0
                    ? imageOnlyIndices[imageOnlyIndices.length - 1]
                    : imageOnlyIndices[cur - 1];
                  setLightboxIndex(nextIdx);
                }}
                aria-label="Imagen anterior"
                className="absolute left-3 top-1/2 z-10 flex size-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
              >
                <ChevronLeft className="size-6" aria-hidden="true" />
              </button>
            )}

            {/* The image itself */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={mediaItems[lightboxIndex]?.type === 'image'
                ? mediaItems[lightboxIndex].url
                : undefined}
              alt={`Imagen ${lightboxIndex + 1} de ${productTitle} (vista ampliada)`}
              className="max-h-full max-w-full object-contain"
            />

            {/* Next (only if there are 2+ image items) */}
            {imageOnlyIndices.length > 1 && (
              <button
                type="button"
                onClick={() => {
                  const cur = imageOnlyIndices.indexOf(lightboxIndex);
                  const nextIdx = cur < 0 || cur >= imageOnlyIndices.length - 1
                    ? imageOnlyIndices[0]
                    : imageOnlyIndices[cur + 1];
                  setLightboxIndex(nextIdx);
                }}
                aria-label="Imagen siguiente"
                className="absolute right-3 top-1/2 z-10 flex size-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
              >
                <ChevronRight className="size-6" aria-hidden="true" />
              </button>
            )}

            {/* Counter (current/total) */}
            {imageOnlyIndices.length > 1 && (
              <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                {imageOnlyIndices.indexOf(lightboxIndex) + 1} / {imageOnlyIndices.length}
              </span>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProductGallery;
